import { api } from './api';

// Browser notifications: Web Push (VAPID + service worker, works when the site is closed) with a
// fallback to native Notification alerts fired by the bell polling while a tab is open.
// `target` = who owns the subscription: the logged-in customer (default) or a partner centre (manage token).
const KEY = 'browserNotif';

const toUint8 = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const accountTarget = {
  key: KEY,
  save: (sub) => api.post('/account/push-subscriptions', { subscription: sub, lang: localStorage.getItem('lang') || 'fr' }),
  remove: (endpoint) => api.delete('/account/push-subscriptions', { data: { endpoint } }),
  test: () => api.post('/account/push-test').then((r) => r.data),
};

export const centreTarget = (token) => ({
  key: `${KEY}Centre`,
  save: (sub) => api.post(`/fitting/centre/${token}/push-subscriptions`, { subscription: sub }),
  remove: (endpoint) => api.delete(`/fitting/centre/${token}/push-subscriptions`, { data: { endpoint } }),
  test: () => api.post(`/fitting/centre/${token}/push-test`).then((r) => r.data),
});

export const adminTarget = {
  key: `${KEY}Admin`,
  save: (sub) => api.post('/admin/push-subscriptions', { subscription: sub, lang: 'fr' }),
  remove: (endpoint) => api.delete('/admin/push-subscriptions', { data: { endpoint } }),
  test: () => api.post('/admin/push-test').then((r) => r.data),
};

export const browserNotif = {
  supported: () => typeof window !== 'undefined' && 'Notification' in window,
  pushSupported: () => browserNotif.supported() && 'serviceWorker' in navigator && 'PushManager' in window,
  permission: () => (browserNotif.supported() ? Notification.permission : 'unsupported'),
  enabled: (target = accountTarget) => browserNotif.supported() && localStorage.getItem(target.key) === '1' && Notification.permission === 'granted',
  pushActive: (target = accountTarget) => localStorage.getItem(target.key) === '1' && localStorage.getItem(`${target.key}Push`) === '1',

  // Ask permission, then subscribe to Web Push when available (returns the permission state)
  enable: async (target = accountTarget) => {
    if (!browserNotif.supported()) return 'unsupported';
    const p = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (p !== 'granted') return p;
    localStorage.setItem(target.key, '1');
    try { await browserNotif.subscribePush(target); } catch { localStorage.removeItem(`${target.key}Push`); }
    return p;
  },

  subscribePush: async (target = accountTarget) => {
    if (!browserNotif.pushSupported()) return false;
    const { data } = await api.get('/push/vapid-public-key');
    if (!data.enabled) return false;
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toUint8(data.public_key) }));
    await target.save(sub.toJSON());
    localStorage.setItem(`${target.key}Push`, '1');
    return true;
  },

  disable: async (target = accountTarget) => {
    localStorage.removeItem(target.key);
    localStorage.removeItem(`${target.key}Push`);
    try {
      const reg = await navigator.serviceWorker?.getRegistration('/sw.js');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) { await target.remove(sub.endpoint).catch(() => {}); await sub.unsubscribe(); }
    } catch { /* ignore */ }
  },

  // Real push to every subscribed device of the target → {sent, errors, devices, enabled}
  test: (target = accountTarget) => target.test(),

  // In-tab fallback (no Web Push subscription): native alert from the bell polling
  show: (title, body, url) => {
    if (!browserNotif.enabled() || browserNotif.pushActive()) return;
    try {
      const n = new Notification(title, { body, icon: '/favicon.ico', tag: `otopieces-${url}` });
      n.onclick = () => { window.focus(); window.location.assign(url || '/mon-compte/notifications'); n.close(); };
    } catch { /* ignore */ }
  },
};
